/**
 * @file useTextToVoiceAudio.ts
 * @description Audio playback hook for text-to-voice (read-aloud) mode
 *
 * Cartesia removed 2026-07-09: the server now synthesizes read-aloud audio
 * with OpenAI TTS and sends complete WAV segments (format: 'wav') instead of
 * raw 44.1kHz PCM chunks. Each segment is decoded with decodeAudioData —
 * which handles any sample rate — and scheduled back-to-back for gapless
 * playback. Public interface is unchanged.
 */

import { useRef, useCallback, useEffect } from 'react';

interface UseTextToVoiceAudioReturn {
  queueAudioChunk: (base64Data: string) => void;
  onAudioComplete: () => void;
  isPlaying: boolean;
  reset: () => void;
}

export function useTextToVoiceAudio(): UseTextToVoiceAudioReturn {
  const audioContextRef = useRef<AudioContext | null>(null);
  const isPlayingRef = useRef<boolean>(false);
  // Track scheduled time for gapless playback across segments
  const nextScheduledTimeRef = useRef<number>(0);
  const activeSourcesRef = useRef<AudioBufferSourceNode[]>([]);

  const base64ToArrayBuffer = useCallback((base64: string): ArrayBuffer => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }, []);

  /**
   * Decode a WAV segment and schedule it right after whatever is already
   * playing. decodeAudioData resamples to the context rate for us.
   */
  const queueAudioChunk = useCallback((base64Data: string) => {
    (async () => {
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }
        const audioContext = audioContextRef.current;
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        const wavData = base64ToArrayBuffer(base64Data);
        const audioBuffer = await audioContext.decodeAudioData(wavData);

        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);

        const startTime = Math.max(audioContext.currentTime, nextScheduledTimeRef.current);
        nextScheduledTimeRef.current = startTime + audioBuffer.duration;

        isPlayingRef.current = true;
        activeSourcesRef.current.push(source);
        source.onended = () => {
          activeSourcesRef.current = activeSourcesRef.current.filter(s => s !== source);
          if (activeSourcesRef.current.length === 0) {
            isPlayingRef.current = false;
            nextScheduledTimeRef.current = 0;
          }
        };

        source.start(startTime);
      } catch (error) {
        console.error('Error decoding/playing TTS wav segment:', error);
      }
    })();
  }, [base64ToArrayBuffer]);

  const onAudioComplete = useCallback(() => {
    // No action needed — scheduled segments finish on their own
  }, []);

  const reset = useCallback(() => {
    for (const source of activeSourcesRef.current) {
      try { source.stop(); } catch { /* already stopped */ }
    }
    activeSourcesRef.current = [];
    isPlayingRef.current = false;
    nextScheduledTimeRef.current = 0;
  }, []);

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  return {
    queueAudioChunk,
    onAudioComplete,
    isPlaying: isPlayingRef.current,
    reset
  };
}

export default useTextToVoiceAudio;
